/**
 * MAKBUZ OKUYUCU
 * ==============
 * Serbest meslek makbuzunun (e-SMM) PDF'inden makbuz tarihini ve en alttaki
 * "Net Tutar"ı çıkarır; dosyaya makbuz eklerken formu doldurmak için.
 * Hiçbir şey kaydetmez, dosya sunucuya gönderilmez — okuma tarayıcıda yapılır.
 *
 * Taranmış ya da fotoğrafı çekilmiş makbuzda metin yoktur; o durumda
 * okunamadığı söylenir ve tarih / tutar elle girilir.
 */
import { pdfSatirlari, sade } from './ekspertizOku';

/* ---------------- Yardımcılar ---------------- */

const iki = (n) => String(n).padStart(2, '0');

/** "12.09.2026", "12-09-2026", "12/09/2026", "2026-09-12" → "2026-09-12" */
const TARIH_TR = /(\d{1,2})[./-](\d{1,2})[./-](\d{4})/;
const TARIH_ISO = /(\d{4})-(\d{2})-(\d{2})/;
export const tarihBul = (metin) => {
  const s = String(metin ?? '');
  let m = s.match(TARIH_ISO);
  if (m) {
    const [y, a, g] = [+m[1], +m[2], +m[3]];
    if (a >= 1 && a <= 12 && g >= 1 && g <= 31) return `${y}-${iki(a)}-${iki(g)}`;
  }
  m = s.match(TARIH_TR);
  if (m) {
    const [g, a, y] = [+m[1], +m[2], +m[3]];
    if (a >= 1 && a <= 12 && g >= 1 && g <= 31 && y >= 2000 && y <= 2100) return `${y}-${iki(a)}-${iki(g)}`;
  }
  return null;
};

/**
 * Metindeki tutarlar, soldan sağa. Türkçe ("13.674,04") ve İngilizce
 * ("13,674.04" / "13674.04") yazımlar; tarih ve yüzde parçaları atlanır.
 */
const TUTAR_RE = /\d{1,3}(?:\.\d{3})+,\d{2}|\d{1,3}(?:,\d{3})+\.\d{2}|\d+,\d{2}|\d+\.\d{2}/g;
export const tutarlariBul = (metin) => {
  const s = String(metin ?? '')
    .replace(/\d{1,2}[./-]\d{1,2}[./-]\d{4}/g, ' ')   // tarihler
    .replace(/%\s*\d+(?:[.,]\d+)?|\d+(?:[.,]\d+)?\s*%/g, ' '); // oranlar
  return (s.match(TUTAR_RE) || []).map((t) => {
    if (/,\d{2}$/.test(t)) return parseFloat(t.replace(/\./g, '').replace(',', '.'));
    return parseFloat(t.replace(/,/g, ''));
  }).filter((n) => Number.isFinite(n));
};

/* Etiketler sadeleştirilmiş (büyük harf, Türkçe harfsiz) metinde aranır. */
const TARIH_ETIKETLERI = ['MAKBUZ TARIHI', 'DUZENLENME TARIHI', 'DUZENLEME TARIHI', 'BELGE TARIHI', 'FATURA TARIHI', 'ISLEM TARIHI'];
/* "Net Ücret" (brüt − stopaj) ayrı bir satırdır; aranmaz. */
const TUTAR_ETIKETLERI = ['NET TUTAR', 'NET ALINAN', 'NET ODENEN', 'ODENECEK TUTAR', 'TAHSIL EDILEN TUTAR', 'TAHSIL EDILEN TOPLAM'];

/**
 * Etiketin geçtiği satırdan değer alır; satırda yoksa bir alttaki satıra bakar.
 * Birden çok satırda geçerse en alttaki kullanılır (makbuzun altındaki toplam).
 */
function etiketliDeger(satirlar, sadeSatirlar, etiketler, cikar, sonuncu) {
  for (const etiket of etiketler) {
    const sira = [];
    sadeSatirlar.forEach((s, i) => { if (s.includes(etiket)) sira.push(i); });
    if (sonuncu) sira.reverse();
    for (const i of sira) {
      const satir = satirlar[i];
      const sonra = satir.slice(Math.max(0, sadeSatirlar[i].indexOf(etiket)));
      const deger = cikar(sonra) ?? cikar(satirlar[i + 1] || '');
      if (deger != null) return { deger, etiket };
    }
  }
  return null;
}

const ETIKET_ADI = {
  'MAKBUZ TARIHI': 'Makbuz Tarihi', 'DUZENLENME TARIHI': 'Düzenlenme Tarihi', 'DUZENLEME TARIHI': 'Düzenleme Tarihi',
  'BELGE TARIHI': 'Belge Tarihi', 'FATURA TARIHI': 'Fatura Tarihi', 'ISLEM TARIHI': 'İşlem Tarihi',
  'NET TUTAR': 'Net Tutar', 'NET ALINAN': 'Net Alınan', 'NET ODENEN': 'Net Ödenen', 'ODENECEK TUTAR': 'Ödenecek Tutar',
  'TAHSIL EDILEN TUTAR': 'Tahsil Edilen Tutar', 'TAHSIL EDILEN TOPLAM': 'Tahsil Edilen Toplam',
};

/* ---------------- Ayrıştırma ---------------- */

/**
 * PDF satırlarından makbuz tarihi ve net tutar.
 * @returns {{ tarih: string|null, netTutar: number|null, tarihEtiketi: string|null, tutarEtiketi: string|null, uyarilar: string[] }}
 */
export function makbuzAyristir(satirlar) {
  const satir = (satirlar || []).map((s) => String(s ?? ''));
  const sadeSatir = satir.map(sade);
  const uyarilar = [];

  // Tarih: önce bilinen etiketler; yoksa makbuzdaki ilk tarih.
  let tarih = null;
  let tarihEtiketi = null;
  const t = etiketliDeger(satir, sadeSatir, TARIH_ETIKETLERI, tarihBul, false);
  if (t) {
    tarih = t.deger;
    tarihEtiketi = ETIKET_ADI[t.etiket];
  } else {
    for (const s of satir) {
      const d = tarihBul(s);
      if (d) { tarih = d; break; }
    }
    if (tarih) uyarilar.push('Tarih etiketi bulunamadı; makbuzdaki ilk tarih alındı.');
  }

  // Net tutar: en alttaki "Net Tutar" (ya da benzeri) satırının son tutarı.
  let netTutar = null;
  let tutarEtiketi = null;
  const sonTutar = (metin) => {
    const l = tutarlariBul(metin);
    return l.length ? l[l.length - 1] : null;
  };
  const n = etiketliDeger(satir, sadeSatir, TUTAR_ETIKETLERI, sonTutar, true);
  if (n && n.deger > 0) {
    netTutar = Math.round(n.deger * 100) / 100;
    tutarEtiketi = ETIKET_ADI[n.etiket];
  }

  /* KDV kalemleri (2026-10-07): Kasa'daki KDV hesabı her makbuzun KENDİ
     KDV'sini kullanır — şahsa kesilen makbuzda stopaj ve KDV tevkifatı
     yoktur, KDV'nin tamamı ödenir. "Gelir Vergisi Tevkifatı" stopajdır,
     KDV tevkifatı sayılmaz. */
  const satirTutari = (desen, haric) => {
    for (let i = 0; i < sadeSatir.length; i++) {
      const s = sadeSatir[i];
      if (!desen.test(s) || (haric && haric.test(s))) continue;
      const deger = sonTutar(satir[i].slice(Math.max(0, s.search(desen)))) ?? sonTutar(satir[i + 1] || '');
      if (deger != null) return Math.round(deger * 100) / 100;
    }
    return null;
  };
  const brutUcret = satirTutari(/BRUT (UCRET|TUTAR)/, /KDV/);
  const tahsilEdilenKdv = satirTutari(/(TAHSIL EDILEN|ODENECEK|ALINAN) KDV/);
  const kdvTevkifat = satirTutari(/KDV TEVKIF|TEVKIF EDILEN KDV|KDV.*TEVKIFAT/, /GELIR VERGISI|G\.?\s?V\.?\s/);
  const kdv = satirTutari(/(^|[^A-Z])(HESAPLANAN )?KDV\b|KATMA DEGER VERGISI/, /TEVKIF|TAHSIL|ODENECEK|ALINAN|MATRAH|DAHIL|HARIC/);
  let odenecekKdv = null;
  if (tahsilEdilenKdv != null) odenecekKdv = tahsilEdilenKdv;
  else if (kdv != null) odenecekKdv = Math.round((kdv - (kdvTevkifat || 0)) * 100) / 100;
  if (odenecekKdv != null && odenecekKdv < 0) odenecekKdv = null;
  if (brutUcret && kdv != null && Math.abs(kdv - brutUcret * 0.2) > 1) {
    uyarilar.push('KDV brüt ücretin %20\'sini tutmuyor; KDV tutarını kontrol edin.');
  }

  return { tarih, netTutar, tarihEtiketi, tutarEtiketi, uyarilar, brutUcret, kdv, kdvTevkifat, odenecekKdv };
}

/* ---------------- Dosyadan okuma ---------------- */

export const pdfMi = (dosya) =>
  /pdf/i.test(dosya?.type || '') || /\.pdf$/i.test(dosya?.name || '');

/**
 * Seçilen makbuz dosyasını okur.
 * @returns {Promise<{ durum: 'tamam'|'eksik'|'metinYok'|'pdfDegil'|'hata', tarih, netTutar, tarihEtiketi, tutarEtiketi, uyarilar, hata? }>}
 */
export async function makbuzOku(dosya) {
  if (!pdfMi(dosya)) return { durum: 'pdfDegil', tarih: null, netTutar: null, uyarilar: [] };
  try {
    const veri = new Uint8Array(await dosya.arrayBuffer());
    const satirlar = await pdfSatirlari(veri);
    if (!satirlar.length) return { durum: 'metinYok', tarih: null, netTutar: null, uyarilar: [] };
    const sonuc = makbuzAyristir(satirlar);
    return { durum: sonuc.tarih && sonuc.netTutar ? 'tamam' : 'eksik', ...sonuc };
  } catch (e) {
    return { durum: 'hata', tarih: null, netTutar: null, uyarilar: [], hata: e?.message || String(e) };
  }
}
